// The color theme on <html data-theme>, as in Admin (api-core's helpers/color-theme.js).
// The <head> script in src/index.template.html applies the same rule before the first paint: keep the two in step.
export const THEME_SYSTEM = 'system'
export const THEME_LIGHT = 'light'
export const THEME_DARK = 'dark'

export const THEME_PREFERENCES = [THEME_SYSTEM, THEME_LIGHT, THEME_DARK]

// The theme with nothing stored. Light until every page is drawn for dark: only a theme stored by hand shows dark.
export const THEME_DEFAULT = THEME_LIGHT

export const COLOR_THEME_STORAGE_KEY = 'color_theme'

export const THEME_APPLIED_EVENT = 'color-theme-applied'

// Pages other products embed (CRM dialers, pickers, their error pages) stay light whatever the viewer picked
export const LIGHT_ONLY_PATH = /^\/(widgets|salesforce|errors)(\/|$)/

const DARK_QUERY = '(prefers-color-scheme: dark)'

// A pick the browser refused to store (private window, blocked site data) still holds on this page
let unsavedPreference = null

export const getThemePreference = () => {
  if (unsavedPreference) {
    return unsavedPreference
  }
  try {
    const stored = localStorage.getItem(COLOR_THEME_STORAGE_KEY)
    return THEME_PREFERENCES.includes(stored) ? stored : THEME_DEFAULT
  } catch (e) {
    return THEME_DEFAULT
  }
}

export const resolveTheme = (preference) => {
  if (LIGHT_ONLY_PATH.test(window.location.pathname)) {
    return THEME_LIGHT
  }
  if (preference !== THEME_SYSTEM) {
    return preference
  }
  return window.matchMedia(DARK_QUERY).matches ? THEME_DARK : THEME_LIGHT
}

// Switches with every transition off for a frame, so controls with a hover fade change with the rest of the page
const withoutTransitions = (change) => {
  const pause = document.createElement('style')
  pause.textContent = '*,*::before,*::after{transition:none!important}'
  document.head.appendChild(pause)
  change()
  // Reading a style makes the browser apply the change now, while transitions are still off
  window.getComputedStyle(document.body).getPropertyValue('opacity')
  setTimeout(() => pause.remove(), 1)
}

export const applyTheme = (preference = getThemePreference()) => {
  const theme = resolveTheme(preference)
  const root = document.documentElement
  if (root.hasAttribute('data-theme') && root.getAttribute('data-theme') !== theme) {
    withoutTransitions(() => root.setAttribute('data-theme', theme))
  } else {
    root.setAttribute('data-theme', theme)
  }
  window.dispatchEvent(new CustomEvent(THEME_APPLIED_EVENT))
}

export const isDarkTheme = () => document.documentElement.getAttribute('data-theme') === THEME_DARK

// Calls back with whether the page is now dark each time a theme is applied; returns the stop
export const onThemeApplied = (callback) => {
  const listener = () => callback(isDarkTheme())
  window.addEventListener(THEME_APPLIED_EVENT, listener)

  return () => window.removeEventListener(THEME_APPLIED_EVENT, listener)
}

// Calls back now with whether the page is dark, then each time a theme is applied; returns the stop
export const followTheme = (callback) => {
  callback(isDarkTheme())

  return onThemeApplied(callback)
}

const storeThemePreference = (preference) => {
  unsavedPreference = null
  try {
    if (preference === THEME_DEFAULT) {
      localStorage.removeItem(COLOR_THEME_STORAGE_KEY)
    } else {
      localStorage.setItem(COLOR_THEME_STORAGE_KEY, preference)
    }
  } catch (e) {
    unsavedPreference = preference
  }
}

export const setThemePreference = (preference) => {
  storeThemePreference(preference)
  applyTheme(preference)
}

// Calls back with the preference another tab stored; cleared storage (key null) means the default again.
// Returns the stop.
export const onThemePreferenceChange = (callback) => {
  const listener = (event) => {
    if (event.key === COLOR_THEME_STORAGE_KEY || event.key === null) {
      callback(getThemePreference())
    }
  }
  window.addEventListener('storage', listener)

  return () => window.removeEventListener('storage', listener)
}

// Applies the theme, then follows the system while the preference is System, and a pick made in another tab.
// Returns the stop.
export const watchColorTheme = () => {
  applyTheme()
  const query = window.matchMedia(DARK_QUERY)
  const onSystemChange = () => applyTheme()
  // Safari before 14 only has the older addListener
  if (query.addEventListener) {
    query.addEventListener('change', onSystemChange)
  } else {
    query.addListener(onSystemChange)
  }
  const stopSync = onThemePreferenceChange(applyTheme)

  return () => {
    if (query.removeEventListener) {
      query.removeEventListener('change', onSystemChange)
    } else {
      query.removeListener(onSystemChange)
    }
    stopSync()
  }
}
